/**
 * Nursing Home (NH) order helpers — the frontend's single source of truth for
 * "is this an NH order?" and "which home is it going to?" (GP-521 / GP-1314).
 *
 * NH is a third fulfilment mode alongside Delivery and Pickup. These orders
 * deliberately carry NO courier `deliveryType`: the backend short-circuits them
 * out of Descartes scheduling entirely (`descartes-scheduling.util.ts`). Any
 * queue column that renders a delivery type therefore has to branch on the
 * fulfilment mode instead of falling through to "Not Set" — never by writing a
 * delivery type onto the order.
 *
 * Mirrors `backend-v2/src/orders/utils/nursing-home.util.ts`. Do NOT write an
 * inline `orderType === 'Nursing Home'` comparison anywhere else; a missed site
 * silently presents an NH order as a courier delivery.
 */

/** The `OrderType.NURSING_HOME` wire value as the API serialises it. */
export const NURSING_HOME_ORDER_TYPE = 'Nursing Home';

/**
 * Minimal shape a grid row needs for these checks. Deliberately structural so
 * every queue's row type (each queue maps its own) satisfies it without a cast.
 */
export interface NursingHomeOrderRowLike {
  orderType?: string | null;
  metadata?: {
    krollGroup?: { groupName?: string | null } | null;
  } | null;
}

/** The order's fulfilment mode is Nursing Home drop-off. */
export function isNursingHomeOrder(
  row: NursingHomeOrderRowLike | null | undefined,
): boolean {
  return row?.orderType === NURSING_HOME_ORDER_TYPE;
}

/**
 * The name of the Kroll group (nursing home) stamped on the order, or
 * `undefined` when the order carries no stamp.
 *
 * NO FALLBACK BY DESIGN: an unstamped order must render without a home name
 * rather than with a guessed or placeholder one — a wrong home name on a
 * fulfilment worksheet routes medication to the wrong building. Callers omit
 * the name when this returns `undefined`.
 *
 * Note the stamp is present on Delivery/Pickup orders too (it marks the
 * PATIENT's membership, which is universal); branch behaviour on
 * `isNursingHomeOrder` and use this only to read the home's name.
 */
export function getNursingHomeGroupName(
  row: NursingHomeOrderRowLike | null | undefined,
): string | undefined {
  const name = row?.metadata?.krollGroup?.groupName;
  if (typeof name !== 'string') return undefined;
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
